(() => {
  if (customElements.get('product-form')) {
    return;
  }

  // ---------------------------------------------------------------------------
  // BundleBuilder — custom element for the interactive bundle slot UI
  // ---------------------------------------------------------------------------
  class BundleBuilder extends HTMLElement {
    constructor() {
      super();
      this.discount1 = parseInt(this.dataset.discountOne, 10) || 0;
      this.discount2 = parseInt(this.dataset.discountTwo, 10) || 0;

      try {
        const dataScript = this.querySelector('.bundle-builder__data');
        this.bundleData = dataScript ? JSON.parse(dataScript.textContent).slots : [];
      } catch (e) {
        this.bundleData = [];
      }

      try {
        const mainScript = this.querySelector('.bundle-builder__main-variants');
        this.mainVariants = mainScript ? JSON.parse(mainScript.textContent) : [];
      } catch (e) {
        this.mainVariants = [];
      }

      this.totalsBlock   = this.querySelector('.bundle-builder__totals');
      this.totalsLabel   = this.querySelector('.bundle-builder__totals-label');
      this.totalsPrice   = this.querySelector('.bundle-builder__totals-price');
      this.totalsCompare = this.querySelector('.bundle-builder__totals-compare');

      // Wire each trigger to open its own corresponding slot panel
      this.querySelectorAll('.bundle-builder__trigger').forEach(trigger => {
        trigger.addEventListener('click', (e) => {
          if (e.target.closest('.bundle-builder__trigger-select .bundle-builder__trigger-icon')) {
            e.preventDefault();
            e.stopPropagation();
            this.deselectSlot(trigger);
            return;
          }
          this.openSlot(trigger);
        });
      });

      this.querySelectorAll('.bundle-builder__select--product').forEach(select => {
        select.addEventListener('change', e => this.onProductChange(e.target));
      });

      this._productChoicesInited = false;
      if (typeof window.Choices === 'function') {
        this.initProductChoices();
      } else {
        window.addEventListener('load', () => this.initProductChoices());
      }

      this.querySelectorAll('.bundle-builder__select--variant').forEach(select => {
        select.addEventListener('change', () => this.updateTotals());
      });

      // Variant-id input is inside <bundle-builder>; add button is in the PDP product form
      const mainVariantInput =
        this.querySelector('input[name="variant-id"]') ||
        this.closest('[data-main-product]')?.querySelector('product-form form input[name="id"]');
      if (mainVariantInput) {
        mainVariantInput.addEventListener('change', () => this.updateTotals());
      }

      const mainForm = this.closest('[data-main-product]')?.querySelector('product-form form');
      const addButton = mainForm?.querySelector('.product-selector__submit.no-js-hidden');
      if (addButton) {
        addButton.addEventListener('click', (e) => this.onSubmitIntercept(e), true);
      }

      const bundleNotice = this.querySelector('.bundle-builder__totals .price__notice');
      if (bundleNotice) {
        const productPrice = this.closest('[data-main-product]')?.querySelector('.product__price');
        productPrice?.querySelector('.price__notice')?.remove();
      }
    }

    onSubmitIntercept(e) {
      const bundleVariantIds = this.getSelectedVariants().map((id) => parseInt(id, 10));
      if (bundleVariantIds.length === 0) return;

      e.preventDefault();
      e.stopImmediatePropagation();
      this.doBundleAddToCart(bundleVariantIds);
    }

    doBundleAddToCart(bundleVariantIds) {
      const productForm = this.closest('[data-main-product]')?.querySelector('product-form');
      const mainForm = productForm?.querySelector('form');
      const mainVariantInput =
        this.querySelector('input[name="variant-id"]') ||
        mainForm?.querySelector('input[name="id"]');
      const mainVariantId = mainVariantInput ? parseInt(mainVariantInput.value, 10) : null;

      const items = [];
      if (mainVariantId) items.push({ id: mainVariantId, quantity: 1 });
      bundleVariantIds.forEach((id) => items.push({ id, quantity: 1 }));

      const payload = { items };

      const addButton = mainForm?.querySelector('[name="add"]');
      if (addButton) addButton.classList.add('disabled');

      fetch("/cart/add.js", {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      })
        .then((r) => r.json())
        .then((response) => {
          if (response.status) {
            const errorWrapper = productForm?.querySelector('[data-error-wrapper]');
            if (errorWrapper) {
              errorWrapper.classList.remove('hidden');
              errorWrapper.textContent = response.description || '';
            }
            return;
          }
          if (typeof window.AMP_API?.OPEN_CART === 'function') window.AMP_API.OPEN_CART();
        })
        .catch(console.error)
        .finally(() => {
          if (addButton) addButton.classList.remove('disabled');
        });
    }

    initProductChoices() {
      if (this._productChoicesInited || typeof window.Choices !== 'function') return;
      const productSelects = this.querySelectorAll('.bundle-builder__select--product');
      if (!productSelects.length) return;

      const decodeAmp = (str) => (str || '').replace(/&amp;/g, '&');

      const thumbTemplates = function (strToEl) {
        return {
          choice: function (config, choice, selectText) {
            const div = document.createElement('div');
            div.id = choice.elementId;
            div.className = `${config.classNames.item} ${config.classNames.itemChoice}`;
            if (choice.selected) div.classList.add(config.classNames.selectedState);
            if (choice.placeholder) div.classList.add(config.classNames.placeholder);
            div.setAttribute('role', choice.groupId > 0 ? 'treeitem' : 'option');
            Object.assign(div.dataset, { choice: '', id: choice.id, value: choice.value, selectText: selectText || config.itemSelectText });
            if (choice.disabled) {
              div.classList.add(config.classNames.itemDisabled);
              div.dataset.choiceDisabled = '';
              div.setAttribute('aria-disabled', 'true');
            } else {
              div.classList.add(config.classNames.itemSelectable);
              div.dataset.choiceSelectable = '';
            }
            if (choice.customProperties && choice.customProperties.thumb) {
              const img = document.createElement('img');
              img.className = 'choices__thumb';
              img.src = choice.customProperties.thumb;
              img.alt = '';
              div.appendChild(img);
            }
            const label = document.createElement('span');
            label.className = 'choices__label';
            label.textContent = decodeAmp(choice.label);
            if (choice.label) label.setAttribute('title', decodeAmp(choice.label));
            div.appendChild(label);
            return div;
          },
          item: function (config, item, removeItemButton) {
            const div = document.createElement('div');
            div.className = config.classNames.item;
            div.dataset.item = '';
            div.dataset.id = item.id;
            div.dataset.value = item.value;
            if (item.customProperties) div.dataset.customProperties = JSON.stringify(item.customProperties);
            if (item.active) div.setAttribute('aria-selected', 'true');
            if (item.disabled) div.setAttribute('aria-disabled', 'true');
            if (item.placeholder) div.classList.add(config.classNames.placeholder);
            div.classList.add(item.highlighted ? config.classNames.highlightedState : config.classNames.itemSelectable);
            if (item.customProperties && item.customProperties.thumb) {
              const img = document.createElement('img');
              img.className = 'choices__thumb';
              img.src = item.customProperties.thumb;
              img.alt = '';
              div.appendChild(img);
            }
            const label = document.createElement('span');
            label.className = 'choices__label';
            label.textContent = decodeAmp(item.label);
            if (item.label) label.setAttribute('title', decodeAmp(item.label));
            div.appendChild(label);
            if (removeItemButton) {
              const btn = document.createElement('button');
              btn.type = 'button';
              btn.className = config.classNames.button;
              btn.setAttribute('aria-label', `Remove item: '${item.value}'`);
              btn.dataset.button = '';
              div.appendChild(btn);
            }
            return div;
          }
        };
      };

      productSelects.forEach((select) => {
        if (select.closest('.choices')) return;
        try {
          const instance = new window.Choices(select, {
            searchEnabled: false,
            allowHTML: false,
            itemSelectText: '',
            callbackOnCreateTemplates: thumbTemplates
          });
          select._choicesInstance = instance;
        } catch (err) {
          console.warn('Bundle builder Choices init:', err);
        }
      });

      this._productChoicesInited = true;
    }

    initVariantChoices(variantSelect) {
      if (typeof window.Choices !== 'function' || variantSelect.closest('.choices')) return;
      const builder = this;
      try {
        const instance = new window.Choices(variantSelect, {
          searchEnabled: false,
          allowHTML: true,
          itemSelectText: '',
          callbackOnChange: function() {
            builder.updateTotals();
          }
        });
        variantSelect._choicesInstance = instance;
      } catch (err) {
        console.warn('Bundle builder variant Choices init:', err);
      }
    }

    destroyVariantChoices(variantSelect) {
      const choicesEl = variantSelect.closest('.choices') || variantSelect.parentElement;
      if (choicesEl) choicesEl.classList.remove('choices--single-variant');
      const instance = variantSelect._choicesInstance;
      if (instance && typeof instance.destroy === 'function') {
        instance.destroy();
        variantSelect._choicesInstance = null;
      }
    }

    openSlot(trigger) {
      const slot = trigger.dataset.slot;
      const slotsPanel = this.querySelector(
        `.bundle-builder__slots[data-slot="${slot}"]`
      );
      if (!slotsPanel) return;

      trigger.setAttribute('aria-expanded', 'true');
      slotsPanel.classList.remove('hidden');
      slotsPanel.setAttribute('aria-hidden', 'false');
      trigger.closest('.bundle-builder__wrap').classList.add('bundle-builder__wrap--open');
    }

    deselectSlot(trigger) {
      const wrap = trigger.closest('.bundle-builder__wrap');
      if (!wrap) return;
      const slot = wrap.dataset.slot;
      const productSelect = this.querySelector(
        `.bundle-builder__select--product[data-slot="${slot}"]`
      );
      const variantSelect = this.querySelector(
        `.bundle-builder__select--variant[data-slot="${slot}"]`
      );

      if (productSelect) {
        productSelect.value = '';
        const productChoices = productSelect._choicesInstance;
        if (productChoices && typeof productChoices.setChoiceByValue === 'function') {
          productChoices.setChoiceByValue('');
        }
      }

      if (variantSelect) {
        this.destroyVariantChoices(variantSelect);
        variantSelect.innerHTML = '<option value="">— Select a size —</option>';
        variantSelect.disabled = true;
      }

      const slotsPanel = this.querySelector(
        `.bundle-builder__slots[data-slot="${slot}"]`
      );
      if (slotsPanel) {
        trigger.setAttribute('aria-expanded', 'false');
        slotsPanel.classList.add('hidden');
        slotsPanel.setAttribute('aria-hidden', 'true');
        wrap.classList.remove('bundle-builder__wrap--open');
      }

      this.updateTotals();
    }

    onProductChange(productSelect) {
      const slot = productSelect.dataset.slot;
      const variantSelect = this.querySelector(
        `.bundle-builder__select--variant[data-slot="${slot}"]`
      );
      if (!variantSelect) return;

      this.destroyVariantChoices(variantSelect);

      const productId = parseInt(productSelect.value, 10);
      const slotData = this.bundleData.find(s => String(s.slot) === String(slot));
      const productData = slotData?.products.find(p => p.id === productId);

      variantSelect.innerHTML = '<option value="">— Select a size —</option>';

      if (productData) {
        productData.variants.forEach(v => {
          const opt = document.createElement('option');
          opt.value = v.id;
          opt.dataset.price = v.price;
          opt.textContent = v.title === 'Default Title' ? 'One Size' : v.title;
          if (!v.available) {
            opt.disabled = true;
            opt.textContent += ' — Sold out';
          }
          variantSelect.appendChild(opt);
        });
        variantSelect.disabled = false;

        const availableOptions = Array.from(variantSelect.options).filter(opt => !opt.disabled && opt.value !== '');
        if (availableOptions.length === 1) {
          variantSelect.value = String(availableOptions[0].value);
        }

        if (typeof window.Choices === 'function') {
          this.initVariantChoices(variantSelect);
          if (availableOptions.length === 1) {
            const instance = variantSelect._choicesInstance;
            if (instance && typeof instance.setChoiceByValue === 'function') {
              instance.setChoiceByValue(variantSelect.value);
            }
            const choicesEl = variantSelect.closest('.choices') || variantSelect.parentElement;
            if (choicesEl) choicesEl.classList.add('choices--single-variant');
          }
        }
      } else {
        variantSelect.disabled = true;
      }

      this.updateTotals();
    }

    getSelectedVariants() {
      return Array.from(
        this.querySelectorAll('.bundle-builder__select--variant')
      )
        .map(s => s.value)
        .filter((value) => value != null && String(value).trim() !== '');
    }

    formatMoney(cents) {
      const amount = Number(cents);
      if (!Number.isFinite(amount)) return '';
      const fmt = (window.theme?.settings?.money_format || '${{amount}}')
        .replace('{{amount}}', (amount / 100).toFixed(2))
        .replace('{{amount_no_decimals}}', Math.round(amount / 100))
        .replace('{{amount_with_comma_separator}}', (amount / 100).toFixed(2).replace('.', ','))
        .replace('{{amount_no_decimals_with_comma_separator}}', Math.round(amount / 100));
      return fmt;
    }

    getVariantPrice(variantId) {
      for (const slotData of this.bundleData) {
        for (const product of slotData.products) {
          const variant = product.variants.find(v => String(v.id) === String(variantId));
          if (variant) return variant.price;
        }
      }
      return 0;
    }

    getMainPrice() {
      const idInput =
        this.querySelector('input[name="variant-id"]') ||
        this.closest('[data-main-product]')?.querySelector('product-form form input[name="id"]');
      const variantId = idInput ? parseInt(idInput.value, 10) : null;
      if (variantId == null) return 0;
      const main = this.mainVariants.find(v => v.id === variantId);
      return main ? main.price : 0;
    }

    updateTotals() {
      if (!this.totalsBlock || !this.totalsPrice) return;

      const selectedVariants = this.getSelectedVariants();
      const bundleCount = selectedVariants.length;
      const totalItems = bundleCount + 1;

      if (bundleCount === 0) {
        this.totalsBlock.classList.add('hidden');
        return;
      }

      const discountPercent = bundleCount === 1 ? this.discount1 : this.discount2;
      const discountMultiplier = 1 - (discountPercent / 100);

      const mainPrice = this.getMainPrice();
      const mainDiscounted = Math.ceil(mainPrice * discountMultiplier);

      const bundleDiscountedTotal = selectedVariants.reduce((sum, id) => {
          const itemPrice = this.getVariantPrice(id);
          return sum + Math.ceil(itemPrice * discountMultiplier);
      }, 0);

      const finalSavedPrice = mainDiscounted + bundleDiscountedTotal;

      const fullPriceUnrounded = mainPrice + selectedVariants.reduce((sum, id) => {
          return sum + this.getVariantPrice(id);
      }, 0);

      if (this.totalsLabel) {
          this.totalsLabel.textContent = `Total (${totalItems} item${totalItems !== 1 ? 's' : ''}): `;
      }

      this.totalsPrice.textContent = this.formatMoney(finalSavedPrice);

      if (this.totalsCompare) {
          this.totalsCompare.textContent = fullPriceUnrounded !== finalSavedPrice ? this.formatMoney(fullPriceUnrounded) : '';
          this.totalsCompare.style.display = fullPriceUnrounded !== finalSavedPrice ? '' : 'none';
      }
      this.totalsBlock.classList.remove('hidden');
    }
  }

  if (!customElements.get('bundle-builder')) {
    customElements.define('bundle-builder', BundleBuilder);
  }

  class ProductForm extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('form');
      this.submitButton = this.form.querySelector('[name="add"]');
      this.cartDrawer = document.querySelector('cart-drawer');
      this.productBar = document.querySelector(
        `#ProductBar-${this.dataset.sectionId}`
      );

      this.form.addEventListener(
        'submit',
        this.onSubmitHandler.bind(this)
      );
    }

    onSubmitHandler(event) {
      //debugger
      event.preventDefault();
      const purchaseOptions = document.querySelector(
        'purchase-options'
      );
      if (
        !!purchaseOptions &&
        !purchaseOptions.isOneTimePurchase() &&
        !this.hasAttribute('data-has-selling-plan')
      ) {
        purchaseOptions.showError();
        return;
      }

      this.submitButton.classList.add('disabled');

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      const formData = new FormData(this.form);
      formData.append(
        'sections',
        this.cartDrawer
          .getSectionsToRender()
          .map(section => section.section)
      );
      formData.append('sections_url', window.location.pathname);
      config.body = formData;

      fetch(`${routes.cart_add_url}`, config)
        .then(response => response.json())
        .then(response => {
          //debugger
          if (response.status) {
            this.handleErrorMessage(response.description);
            return;
          }

          this.cartDrawer.renderContents(response);
        })
        .catch(error => {
          console.error(error);
        })
        .finally(() => {
          this.submitButton.classList.remove('disabled');
        });
    }

    handleErrorMessage(errorMessage = false) {
      const errorWrapper = this.querySelector('[data-error-wrapper]');
      if (!errorWrapper) return;

      let message = errorMessage;

      if (typeof errorMessage === 'object' && errorMessage !== null) {
        message = Object.values(errorMessage).flat().join(', ');
      }

      errorWrapper.classList.toggle('hidden', !message);
      errorWrapper.textContent = message || '';
    }


    toggleAddButton(disable, text) {
      [
        this.submitButton,
        this.productBar?.querySelector('[name="add"]')
      ].forEach(submitButton => {
        if (disable) {
          submitButton?.setAttribute('disabled', 'disabled');
          if (text && submitButton) submitButton.textContent = text;
        } else if (submitButton) {
          submitButton.removeAttribute('disabled');
          submitButton.textContent = this.hasAttribute(
            'data-has-selling-plan'
          )
            ? window.variantStrings.addSubscriptionToCart
            : window.variantStrings.addToCart;
        }
      });
    }
  }

  customElements.define('product-form', ProductForm);
})();
